import { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Search,
  Ticket,
  ToggleLeft,
  ToggleRight,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError } from "@/lib/adminApi";
import {
  EmptyState,
  GhostButton,
  PageHeader,
  Panel,
  PrimaryButton,
  shortDate,
} from "@/components/admin/uiKit";

const PLAN_OPTIONS = [
  ["monthly", "Monthly"],
  ["biweekly", "Bi-Weekly"],
  ["onetime", "One-Time"],
];

const SOURCE_OPTIONS = [
  "signup_finalize",
  "webhook_safety_net",
  "stripe_webhook",
  "admin_manual",
];

const PLAN_LABEL = {
  monthly: "Monthly",
  biweekly: "Bi-Weekly",
  onetime: "One-Time",
};

const EMPTY = {
  code: "",
  discount_percent: 25,
  applies_to: [],
  duration: "once",
  new_customers_only: true,
  max_redemptions_per_customer: 1,
  active: true,
};

export default function AdminCoupons() {
  const [tab, setTab] = useState("coupons");
  const [items, setItems] = useState(null);
  const [redemptionCounts, setRedemptionCounts] = useState({});
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await adminApi.get("/admin/coupons");
      setItems(data.items || []);
      setRedemptionCounts({});
    } catch (err) {
      toast.error(formatAdminError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (item) => {
    try {
      if (item.active) {
        await adminApi.delete(`/admin/coupons/${item.id}`);
      } else {
        await adminApi.patch(`/admin/coupons/${item.id}`, { active: true });
      }
      toast.success(item.active ? "Disabled" : "Enabled");
      load();
    } catch (err) {
      toast.error(formatAdminError(err));
    }
  };

  return (
    <div data-testid="admin-coupons">
      <PageHeader
        overline="Marketing"
        title="Coupons"
        description="Create promo codes that can be applied at checkout. CANS25 ships enabled — 25% off first payment, applies to all packages, one use per customer."
        actions={
          tab === "coupons" ? (
            <PrimaryButton onClick={() => setEditing({ ...EMPTY })} data-testid="admin-coupons-new">
              <Plus size={14} /> New coupon
            </PrimaryButton>
          ) : null
        }
      />

      {/* Sub-tabs */}
      <div
        className="mb-5 inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1"
        role="tablist"
        data-testid="admin-coupons-tabs"
      >
        {[
          { id: "coupons", label: "Coupons" },
          { id: "redemptions", label: "Redemptions" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`px-3.5 py-1.5 rounded-md text-xs font-bold uppercase tracking-[0.16em] transition-colors ${
              tab === t.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid={`admin-coupons-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "coupons" && (
        <>
          {items === null ? (
            <Panel className="p-10 text-center">
              <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
            </Panel>
          ) : items.length === 0 ? (
            <EmptyState
              title="No coupons configured"
              description="Add your first promo code to drive new signups."
              action={
                <PrimaryButton onClick={() => setEditing({ ...EMPTY })}>
                  <Plus size={14} /> New coupon
                </PrimaryButton>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((c) => (
            <article
              key={c.id}
              className="rounded-xl bg-white border border-slate-200 overflow-hidden flex flex-col p-5 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.06)]"
              data-testid={`admin-coupon-card-${c.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-amber-100 text-amber-700">
                    <Ticket size={18} />
                  </span>
                  <div>
                    <p
                      className="text-2xl font-extrabold text-slate-900 tracking-tight"
                      style={{ fontFamily: "Outfit, ui-sans-serif" }}
                    >
                      {c.code}
                    </p>
                    <p className="text-xs text-slate-500">
                      {c.discount_percent}% off · {(c.applies_to || []).join(", ") || "All packages"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggle(c)}
                  className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-md ${
                    c.active
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}
                  data-testid={`admin-coupon-toggle-${c.id}`}
                >
                  {c.active ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                  {c.active ? "Live" : "Off"}
                </button>
              </div>

              <ul className="text-sm text-slate-600 space-y-1.5 mb-4">
                <li>Duration: <strong>{c.duration}</strong></li>
                <li>New customers only: <strong>{c.new_customers_only ? "Yes" : "No"}</strong></li>
                <li>Per-customer cap: <strong>{c.max_redemptions_per_customer}</strong></li>
                <li className="text-xs text-slate-500">Created {shortDate(c.created_at)}</li>
              </ul>

              <div className="mt-auto pt-3 flex items-center gap-2">
                <GhostButton onClick={() => setEditing(c)} data-testid={`admin-coupon-edit-${c.id}`}>
                  <Pencil size={12} /> Edit
                </GhostButton>
              </div>
            </article>
          ))}
        </div>
      )}
        </>
      )}

      {tab === "redemptions" && <RedemptionsTab />}

      {editing && (
        <CouponForm
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function CouponForm({ item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({ ...EMPTY, ...item });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const togglePlan = (plan) => {
    setForm((f) => {
      const list = new Set(f.applies_to || []);
      if (list.has(plan)) list.delete(plan);
      else list.add(plan);
      return { ...f, applies_to: Array.from(list) };
    });
  };

  const save = async () => {
    if (!form.code.trim()) {
      toast.error("Coupon code is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        discount_percent: parseInt(form.discount_percent, 10),
        applies_to: form.applies_to,
        duration: form.duration,
        new_customers_only: !!form.new_customers_only,
        max_redemptions_per_customer: parseInt(form.max_redemptions_per_customer, 10),
        active: !!form.active,
      };
      if (isNew) {
        await adminApi.post("/admin/coupons", {
          code: form.code.trim().toUpperCase(),
          ...payload,
        });
        toast.success("Coupon created");
      } else {
        await adminApi.patch(`/admin/coupons/${item.id}`, payload);
        toast.success("Saved");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatAdminError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="admin-coupon-form">
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
      />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">
            {isNew ? "New coupon" : "Edit coupon"}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 grid place-items-center"
          >
            <X size={16} />
          </button>
        </header>

        <div className="p-6 space-y-4">
          <FormRow label="Code">
            <input
              className="form-input uppercase"
              value={form.code}
              onChange={(e) => update("code", e.target.value.toUpperCase())}
              disabled={!isNew}
              data-testid="admin-coupon-input-code"
            />
          </FormRow>
          <div className="grid grid-cols-2 gap-3">
            <FormRow label="Discount %">
              <input
                type="number"
                min="1"
                max="100"
                className="form-input"
                value={form.discount_percent}
                onChange={(e) => update("discount_percent", e.target.value)}
                data-testid="admin-coupon-input-discount"
              />
            </FormRow>
            <FormRow label="Per-customer cap">
              <input
                type="number"
                min="1"
                max="20"
                className="form-input"
                value={form.max_redemptions_per_customer}
                onChange={(e) => update("max_redemptions_per_customer", e.target.value)}
              />
            </FormRow>
          </div>
          <FormRow label="Applies to">
            <div className="flex flex-wrap gap-2">
              {PLAN_OPTIONS.map(([v, l]) => {
                const active = (form.applies_to || []).includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => togglePlan(v)}
                    className={`text-xs font-bold uppercase tracking-[0.18em] px-3 py-2 rounded-lg border transition-colors ${
                      active
                        ? "bg-sky-600 text-white border-sky-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          </FormRow>
          <FormRow label="Duration">
            <select
              className="form-input"
              value={form.duration}
              onChange={(e) => update("duration", e.target.value)}
            >
              <option value="once">Once</option>
              <option value="repeating">Repeating</option>
              <option value="forever">Forever</option>
            </select>
          </FormRow>
          <FormRow label="New customers only">
            <label className="form-input flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.new_customers_only}
                onChange={(e) => update("new_customers_only", e.target.checked)}
              />
              <span className="text-sm text-slate-700">Yes, restrict to first-time signups</span>
            </label>
          </FormRow>
          <FormRow label="Active">
            <label className="form-input flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => update("active", e.target.checked)}
              />
              <span className="text-sm text-slate-700">Yes, accept this code at checkout</span>
            </label>
          </FormRow>
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center gap-2">
          <PrimaryButton onClick={save} disabled={saving} data-testid="admin-coupon-save">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </PrimaryButton>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
        </footer>

        <style>{`
          .form-input {
            width: 100%;
            min-height: 2.75rem;
            border-radius: 0.5rem;
            border: 1px solid #E2E8F0;
            background: #fff;
            padding: 0.5rem 0.75rem;
            font-size: 0.875rem;
            color: #0F172A;
          }
          .form-input:focus {
            outline: none;
            border-color: #38BDF8;
            box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
          }
        `}</style>
      </aside>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

// ---------------- Redemptions tab (read-only) -----------------------------
function fmtCents(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtRedeemedAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const date = d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${date} ${time}`;
  } catch {
    return iso;
  }
}

function RedemptionsTab() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  const load = async () => {
    setError(null);
    try {
      const params = { limit: 50 };
      if (planFilter !== "all") params.plan = planFilter;
      if (sourceFilter !== "all") params.source = sourceFilter;
      // Search input is applied client-side across both code + email so the
      // user gets snappy filtering. Server's `email`/`code` params are also
      // wired but we use them only when the user opts in via dedicated UI.
      const { data } = await adminApi.get("/admin/coupon-redemptions", { params });
      setItems(data.items || []);
    } catch (err) {
      setItems([]);
      setError(formatAdminError(err));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilter, sourceFilter]);

  const filtered = useMemo(() => {
    if (!items) return null;
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => {
      const code = (r.code || "").toLowerCase();
      const email = (r.customer_email || "").toLowerCase();
      return code.includes(q) || email.includes(q);
    });
  }, [items, search]);

  const reset = () => {
    setSearch("");
    setPlanFilter("all");
    setSourceFilter("all");
  };

  return (
    <div data-testid="admin-redemptions">
      {/* Filters */}
      <Panel className="p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div className="lg:col-span-2">
            <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
              Search code or email
            </label>
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="CANS25 or jane@example.com"
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                data-testid="admin-redemptions-search"
              />
            </div>
          </div>
          <FilterSelect
            label="Plan"
            value={planFilter}
            onChange={setPlanFilter}
            options={[["all", "All"], ...PLAN_OPTIONS]}
            testId="admin-redemptions-plan"
          />
          <FilterSelect
            label="Source"
            value={sourceFilter}
            onChange={setSourceFilter}
            options={[["all", "All"], ...SOURCE_OPTIONS.map((s) => [s, s])]}
            testId="admin-redemptions-source"
          />
        </div>
        <div className="flex items-center justify-between mt-3">
          <p className="text-[11px] text-slate-500">
            {filtered === null
              ? "Loading…"
              : `${filtered.length} ${filtered.length === 1 ? "redemption" : "redemptions"} shown`}
          </p>
          <GhostButton onClick={reset} data-testid="admin-redemptions-reset">
            <RotateCcw size={12} /> Reset
          </GhostButton>
        </div>
      </Panel>

      {error && (
        <Panel className="p-4 mb-4 border-red-200 bg-red-50/40 text-sm text-red-700">
          {error}
        </Panel>
      )}

      {filtered === null ? (
        <Panel className="p-10 text-center">
          <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
        </Panel>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No redemptions yet."
          description={
            items && items.length > 0
              ? "No rows match those filters."
              : "Once customers apply CANS25 (or any other promo) at checkout, redemptions show up here."
          }
        />
      ) : (
        <RedemptionsList items={filtered} />
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full h-10 pl-3 pr-9 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 capitalize"
          data-testid={testId}
        >
          {options.map(([v, l]) => (
            <option key={v} value={v} className="capitalize">
              {l}
            </option>
          ))}
        </select>
        <ChevronDown
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
      </div>
    </div>
  );
}

function RedemptionsList({ items }) {
  return (
    <Panel className="overflow-hidden">
      {/* Desktop / tablet table — hides under sm */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="admin-redemptions-table">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-left text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3 text-right">Original</th>
                <th className="px-4 py-3 text-right">Discount</th>
                <th className="px-4 py-3 text-right">Final</th>
                <th className="px-4 py-3">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-slate-50/60"
                  data-testid={`admin-redemption-row-${r.id}`}
                >
                  <td
                    className="px-4 py-3 text-slate-700 whitespace-nowrap"
                    title={r.redeemed_at || ""}
                  >
                    {fmtRedeemedAt(r.redeemed_at)}
                  </td>
                  <td className="px-4 py-3 text-slate-900 font-medium">
                    {r.customer_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 truncate max-w-[200px]" title={r.customer_email || ""}>
                    {r.customer_email || "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-900 font-bold">
                    {r.code || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {r.plan ? (PLAN_LABEL[r.plan] || r.plan) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 tabular-nums">
                    {fmtCents(r.original_amount_cents)}
                  </td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-semibold tabular-nums">
                    {r.discount_amount_cents
                      ? `-${fmtCents(r.discount_amount_cents)}`
                      : fmtCents(r.discount_amount_cents)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-900 font-semibold tabular-nums">
                    {fmtCents(r.final_amount_cents)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-[11px] font-mono text-slate-700">
                      {r.source}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile stacked cards */}
      <div className="sm:hidden divide-y divide-slate-100">
        {items.map((r) => (
          <div
            key={r.id}
            className="p-4"
            data-testid={`admin-redemption-card-${r.id}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-base font-bold text-slate-900">
                {r.code || "—"}
              </span>
              <span className="text-xs text-slate-500">
                {fmtRedeemedAt(r.redeemed_at)}
              </span>
            </div>
            <p className="text-sm text-slate-900 font-medium truncate">
              {r.customer_name || "—"}
            </p>
            <p className="text-xs text-slate-500 truncate">
              {r.customer_email || "—"}
            </p>
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400">
                  Plan
                </p>
                <p className="text-slate-700 font-medium">
                  {r.plan ? (PLAN_LABEL[r.plan] || r.plan) : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400">
                  Discount
                </p>
                <p className="text-emerald-700 font-semibold tabular-nums">
                  {r.discount_amount_cents
                    ? `-${fmtCents(r.discount_amount_cents)}`
                    : fmtCents(r.discount_amount_cents)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400">
                  Paid
                </p>
                <p className="text-slate-900 font-semibold tabular-nums">
                  {fmtCents(r.final_amount_cents)}
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
              <span>
                Original{" "}
                <span className="text-slate-700 font-medium">
                  {fmtCents(r.original_amount_cents)}
                </span>
              </span>
              <span className="font-mono text-[10px] text-slate-500">
                {r.source}
              </span>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}
